import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import BookingItemDetails from "@/components/site/BookingItemDetails";
import AdminAddonPanel from "@/components/admin/AdminAddonPanel";
import { toast } from "sonner";
import { ArrowLeft, Ban, RefreshCw, Plus, Armchair, Briefcase, Utensils, Package } from "lucide-react";

export default function AdminBookingDetail() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [addonOpen, setAddonOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [addonForm, setAddonForm] = useState({ kind: "seat", segment_index: 0, pax_index: 0, label: "Window", price: 0, note: "" });
  const [refundForm, setRefundForm] = useState({ amount: 0, note: "", kind: "stripe" });

  const load = async () => { const { data } = await api.get(`/admin/bookings/${bookingId}`); setD(data); };
  useEffect(() => { load(); }, [bookingId]); // eslint-disable-line

  if (!d) return <div className="text-slate-500">Loading...</div>;
  const b = d.booking;
  const isFlight = b.booking_type === "flight";

  const sb = (s, kind = "booking") => {
    const palettes = {
      booking: { confirmed: "bg-emerald-100 text-emerald-700",
                 pending_payment: "bg-amber-100 text-amber-700",
                 pending: "bg-amber-100 text-amber-700",
                 cancelled: "bg-red-100 text-red-700",
                 failed: "bg-red-100 text-red-700" },
      ticket:  { issued: "bg-emerald-100 text-emerald-700",
                 pending: "bg-amber-100 text-amber-700",
                 cancelled: "bg-red-100 text-red-700",
                 failed: "bg-red-100 text-red-700" },
    };
    const p = palettes[kind] || {};
    if (!s) return <Badge className="bg-slate-100 text-slate-700 border-0">—</Badge>;
    return <Badge className={`${p[s] || "bg-slate-100 text-slate-700"} border-0 capitalize`}>{String(s).replace(/_/g," ")}</Badge>;
  };

  const doVoid = async () => {
    if (!window.confirm("Void this booking? This cannot be undone.")) return;
    try { await api.post(`/admin/bookings/${b.id}/void`); toast.success("Booking voided"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doCancel = async () => {
    if (!window.confirm("Cancel this booking (admin direct cancel)?")) return;
    try { await api.post(`/admin/bookings/${b.id}/cancel`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const submitAddon = async () => {
    try {
      await api.post(`/admin/bookings/${b.id}/add-addon`, {
        items: [{
          kind: addonForm.kind, segment_index: Number(addonForm.segment_index), pax_index: Number(addonForm.pax_index),
          label: addonForm.label, price: Number(addonForm.price) || 0,
        }],
        note: addonForm.note,
      });
      toast.success("Add-on serviced"); setAddonOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const submitRefund = async () => {
    try {
      await api.post("/admin/refunds", { booking_id: b.id, amount: Number(refundForm.amount), note: refundForm.note, kind: refundForm.kind || "stripe" });
      toast.success("Refund recorded"); setRefundOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <Button variant="ghost" onClick={() => navigate("/admin/bookings")} className="rounded-full"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>

      <Card className="mt-4 overflow-hidden">
        <div className="bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-primary-dark)] p-6 text-white flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--brand-secondary)]">Booking</div>
            <h1 className="font-heading text-2xl font-semibold capitalize">{b.booking_type} · PNR {b.pnr}</h1>
            <div className="text-xs text-teal-50/90 mt-0.5">{b.user_email} · ${Number(b.total_amount).toFixed(2)} {b.currency}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span title={b.failure_reason || ""}>{sb(b.booking_status || b.status, "booking")}</span>
            {b.ticketing_voucher_status && (
              <span title={b.failure_reason || ""}>{sb(b.ticketing_voucher_status, "ticket")}</span>
            )}
          </div>
        </div>

        {/* PNR / Ticket / supplier-error banner — real-data truth strip */}
        <div className="px-5 py-3 bg-slate-50/60 border-b border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="uppercase tracking-wider text-slate-400 font-bold">Txn ID</div>
            <div className="font-mono font-bold text-[var(--brand-primary-dark)] mt-0.5" data-testid="detail-txn-id">{b.internal_ref || b.pnr_internal || b.pnr || "—"}</div>
          </div>
          <div>
            <div className="uppercase tracking-wider text-slate-400 font-bold">PNR</div>
            <div className="font-mono font-bold text-slate-800 mt-0.5" data-testid="detail-real-pnr">{(b.amadeus_locator || b.travelport_locator) || (b.pnr && b.pnr.startsWith("FA") ? "—" : (b.pnr || "—"))}</div>
          </div>
          <div>
            <div className="uppercase tracking-wider text-slate-400 font-bold">Ticket #</div>
            <div className="font-mono font-bold text-slate-800 mt-0.5" data-testid="detail-ticket-no">{b.ticket_no || "—"}</div>
          </div>
          <div>
            <div className="uppercase tracking-wider text-slate-400 font-bold">Supplier</div>
            <div className="font-semibold capitalize text-slate-800 mt-0.5">{b.supplier || b.supplier_source || "—"}</div>
          </div>
        </div>

        {/* Supplier error message strip — visible whenever ticketing failed OR is pending */}
        {(() => {
          const noPnr = !(b.amadeus_locator || b.travelport_locator);
          const noTicket = !(b.ticket_no || (b.amadeus_tickets || []).length > 0 || (b.travelport_tickets || []).length > 0);
          const isFlight = b.booking_type === "flight";
          const supplier = (b.supplier || b.supplier_source || "").toLowerCase();
          const isPaid = (b.payment_status || "").toLowerCase() === "paid" || b.status === "confirmed";
          const shouldShow = isFlight && isPaid && noTicket && (supplier === "amadeus" || supplier === "travelport");
          if (!shouldShow) return null;
          // Pull Amadeus error if present
          const amErr = b.amadeus_create_response?.error;
          const amErrText = amErr
            ? (Array.isArray(amErr?.errors) && amErr.errors[0]
                ? `${amErr.errors[0].title || ""} ${amErr.errors[0].detail || ""}`.trim()
                : (typeof amErr === "string" ? amErr : (amErr.text || JSON.stringify(amErr).slice(0, 200))))
            : "";
          return (
            <div className="px-5 py-4 bg-rose-50 border-b border-rose-200" data-testid="supplier-error-banner">
              <div className="text-xs uppercase tracking-wider text-rose-700 font-bold">
                {noPnr ? "Ticket NOT issued — no PNR" : "PNR created · ticket NOT issued"}
              </div>
              {(() => {
                // The backend's `_enrich_booking_truth` already assembles a
                // pipe-joined `failure_reason` that concatenates every source
                // (segment_errors + error_messages + ticket_failure + amadeus)
                // with a "Ticketing:" / "Amadeus:" label prefix. So when
                // `failure_reason` is present we render ONLY that summary and
                // skip the raw fields to avoid duplicating each sentence twice.
                if (b.failure_reason) {
                  return b.failure_reason.split(" | ").map((line, i) => (
                    <div key={`fr-${i}`} className="mt-1.5 text-sm text-rose-900 font-mono">{line}</div>
                  ));
                }
                // Fallback when backend enrichment hasn't run (e.g., legacy
                // records) — render each raw field individually.
                const rows = [];
                (b.travelport_segment_errors || []).forEach((se, i) => rows.push(
                  <div key={`se-${i}`} className="mt-1.5 text-sm text-rose-900 font-mono">
                    {se.carrier}{se.flight_number} · {se.origin}→{se.destination} · class {se.class}: <span className="font-bold">{se.message}</span>
                  </div>
                ));
                (b.travelport_error_messages || []).forEach((m, i) => rows.push(
                  <div key={`em-${i}`} className="mt-1 text-sm text-rose-900 font-mono">{m}</div>
                ));
                if (b.travelport_ticket_failure?.message) {
                  rows.push(<div key="tf" className="mt-1 text-sm text-rose-900 font-mono">Ticketing: {b.travelport_ticket_failure.message}</div>);
                }
                if (amErrText) {
                  rows.push(<div key="am" className="mt-1 text-sm text-rose-900 font-mono">Amadeus: {amErrText}</div>);
                }
                return rows;
              })()}
              {!b.failure_reason && !amErrText && !(b.travelport_error_messages || []).length && !(b.travelport_segment_errors || []).length && !b.travelport_ticket_failure?.message && (
                <div className="mt-1 text-sm text-rose-900">Booking is paid but the supplier has not returned a ticket yet. Click &quot;Issue ticket&quot; to try again — no new PNR will be created.</div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button data-testid="bk-retry-issuance" size="sm" className="bg-rose-600 hover:bg-rose-700 rounded-full" onClick={async () => {
                  try {
                    const { data } = await api.post(`/admin/bookings/${b.id}/retry-issuance`);
                    if ((data.error_messages || []).length) toast.error(data.error_messages.join(" | "));
                    else toast.success(`Issued — PNR: ${data.carrier_locator || "—"}, Tickets: ${(data.ticket?.tickets || []).length}`);
                    load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Issue failed"); }
                }}><RefreshCw className="w-4 h-4 mr-1" /> Issue ticket</Button>
                <Button data-testid="bk-refund-rebook" size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-100 rounded-full" onClick={async () => {
                  if (!window.confirm("Refund the customer and email them a rebook link? This cancels the booking and processes a Stripe refund.")) return;
                  try {
                    const { data } = await api.post(`/admin/bookings/${b.id}/refund-and-rebook`);
                    toast.success(`Refund issued · ${data.refund?.currency} ${Number(data.refund?.amount || 0).toFixed(2)}. Rebook email queued.`);
                    if (data.rebook_url) {
                      try { await navigator.clipboard.writeText(data.rebook_url); toast.success("Rebook link copied to clipboard"); } catch (_e) { /* clipboard may be blocked */ }
                    }
                    load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Refund & rebook failed"); }
                }}><i className="fa-solid fa-rotate-right mr-1" aria-hidden="true"></i> Refund &amp; Rebook</Button>
              </div>
            </div>
          );
        })()}

        <div className="p-5 flex flex-wrap gap-2 border-b border-slate-100">
          {isFlight && b.status === "confirmed" && (
            <Button data-testid="admin-bk-addon" onClick={() => setAddonOpen(true)} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full"><Plus className="w-4 h-4 mr-1" /> Add seat / bag / meal</Button>
          )}
          {b.status !== "cancelled" && (
            <Button data-testid="admin-bk-void" onClick={doVoid} variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50"><Ban className="w-4 h-4 mr-1" /> Void PNR</Button>
          )}
          {b.status !== "cancelled" && (
            <Button data-testid="admin-bk-cancel" onClick={doCancel} variant="outline" className="rounded-full">Cancel booking</Button>
          )}
          <Button data-testid="admin-bk-refund" onClick={() => setRefundOpen(true)} variant="outline" className="rounded-full"><RefreshCw className="w-4 h-4 mr-1" /> Record refund</Button>
          <Button data-testid="admin-bk-download-logs" onClick={async () => {
            try {
              const token = localStorage.getItem("fa_admin_token") || "";
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/supplier-logs/booking/${b.id}/download`;
              const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
              if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                toast.error(err.detail || `Download failed (HTTP ${resp.status})`); return;
              }
              const blob = await resp.blob();
              const disp = resp.headers.get("content-disposition") || "";
              const m = /filename="([^"]+)"/.exec(disp);
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = m ? m[1] : `supplier-logs-${b.id.slice(0,8)}.zip`;
              document.body.appendChild(a); a.click(); a.remove();
              setTimeout(() => URL.revokeObjectURL(a.href), 1000);
              toast.success("Supplier logs archive downloaded");
            } catch (e) { toast.error("Download failed"); }
          }} variant="outline" className="rounded-full border-sky-200 text-sky-700 hover:bg-sky-50">
            <Package className="w-4 h-4 mr-1" /> Download supplier logs (.zip)
          </Button>
        </div>

        <div className="p-5">
          <BookingItemDetails bookingType={b.booking_type} item={b.item} travelers={b.travelers} />
          <div className="mt-6 pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div><div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Contact</div>{b.contact?.name} · {b.contact?.email}</div>
            <div><div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Payment</div>{b.payment_status} · paid {b.paid_at ? new Date(b.paid_at).toLocaleString() : "—"}</div>
            <div className="md:text-right">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Total</div>
              <div className="font-heading text-xl font-bold text-[var(--brand-accent)]">${Number(b.total_amount).toFixed(2)} {b.currency}</div>
              {b.original_currency && b.original_currency !== b.currency && (
                <div data-testid="booking-fx-trail" className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  Sourced at <span className="font-mono font-semibold text-[var(--brand-accent)]">{Number(b.original_total_amount || 0).toFixed(2)} {b.original_currency}</span>
                  {b.fx_rate_applied != null && (
                    <> · rate <span className="font-mono">{Number(b.fx_rate_applied).toFixed(6)}</span></>
                  )}
                  {b.fx_margin_pct_applied != null && (
                    <> · FX margin <span className="font-mono font-semibold text-emerald-700">+{Number(b.fx_margin_pct_applied).toFixed(2)}%</span></>
                  )}
                  {b.fx_rate_applied != null && b.fx_margin_pct_applied != null && (
                    <> · effective <span className="font-mono font-semibold text-emerald-700" data-testid="booking-fx-effective">{(Number(b.fx_rate_applied) * (1 + Number(b.fx_margin_pct_applied) / 100)).toFixed(6)}</span></>
                  )}
                  {b.fx_rate_source && (
                    <> · <span className="font-medium capitalize">{b.fx_rate_source.replace(".", " ")}</span></>
                  )}
                </div>
              )}
              {b.promo_applied && b.promo_applied.code && (
                <div data-testid="booking-promo-trail" className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  Promo <span className="font-mono font-bold text-[var(--brand-primary-dark)]">{b.promo_applied.code}</span>
                  {b.promo_applied.discount != null && (
                    <> · <span className="font-mono font-semibold text-emerald-700">−${Number(b.promo_applied.discount).toFixed(2)}</span></>
                  )}
                  {b.promo_applied.applied_on === "base_fare" && b.promo_applied.base_amount != null && (
                    <> · on base ${Number(b.promo_applied.base_amount).toFixed(2)} ({b.promo_applied.amount}%)</>
                  )}
                  {b.promo_applied.applied_on === "flat" && (
                    <> · flat ${b.promo_applied.amount}</>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Add-ons history */}
      {d.addons?.length > 0 && (
        <Card className="mt-5 p-5">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Add-ons on this booking</h2>
            <span className="text-[10px] uppercase tracking-wider bg-teal-100 text-teal-900 px-2 py-1 rounded-full font-bold">
              <i className="fa-solid fa-circle-info mr-1" aria-hidden="true"></i>
              Airline sync triggers automatically after customer payment
            </span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            Pending add-ons live only on our side. Once the customer pays the Stripe link, we fire <code>AirAddSpecialServiceReq</code> (Travelport) so the SSR appears on the carrier&apos;s PNR. Sync status is shown per row.
          </p>
          <ul className="mt-3 space-y-2 text-sm">
            {d.addons.map((a) => (
              <li key={a.id} className="flex items-center gap-3 flex-wrap p-2 rounded-lg hover:bg-slate-50">
                <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                  {(a.items || []).map((it, i) => (
                    <span key={i} className="inline-flex items-center gap-1">
                      {it.kind === "seat" && <Armchair className="w-3.5 h-3.5 text-[var(--brand-primary)]" />}
                      {it.kind === "bag" && <Briefcase className="w-3.5 h-3.5 text-[var(--brand-primary)]" />}
                      {it.kind === "meal" && <Utensils className="w-3.5 h-3.5 text-[var(--brand-primary)]" />}
                      <span className="capitalize">{it.kind}</span> · {it.label} · Seg {Number(it.segment_index)+1} · Pax {Number(it.pax_index)+1}
                      <span className="text-slate-400">${Number(it.price).toFixed(2)}</span>
                    </span>
                  ))}
                </div>
                <Badge className={(a.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : a.payment_status === "refunded" ? "bg-slate-200 text-slate-700" : "bg-amber-100 text-amber-700") + " border-0"}>{a.payment_status}</Badge>
                {(() => {
                  // Supplier sync chip — appears AFTER the customer pays.
                  // States: synced (green) | sync_failed (rose) | not_synced (slate)
                  if (a.payment_status !== "paid") return null;
                  if (a.supplier_pnr_synced) {
                    return (
                      <span data-testid={`addon-supplier-${a.id}`} title={`Synced to airline PNR at ${a.supplier_sync_at ? new Date(a.supplier_sync_at).toLocaleString() : ""}`}
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-800">
                        <i className="fa-solid fa-plane-circle-check" aria-hidden="true"></i>
                        Synced to airline
                      </span>
                    );
                  }
                  if (a.supplier_sync_error) {
                    return (
                      <span data-testid={`addon-supplier-${a.id}`} title={a.supplier_sync_error}
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium bg-rose-100 text-rose-800">
                        <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
                        Airline sync failed
                      </span>
                    );
                  }
                  return (
                    <span data-testid={`addon-supplier-${a.id}`} title="Awaiting supplier push"
                      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-600">
                      <i className="fa-solid fa-clock" aria-hidden="true"></i>
                      Sync pending
                    </span>
                  );
                })()}
                {(() => {
                  // Payment-link telemetry chip: shows the lifecycle Sent → Opened → Paid
                  // so the admin can tell which customers ignored the link vs. opened
                  // but didn't pay (worth a follow-up nudge).
                  const pls = a.payment_link_status;
                  if (!pls) return null;
                  const opens = Number(a.payment_link_opens || 0);
                  const map = {
                    sent:   { label: "Link sent",   cls: "bg-slate-100 text-slate-700",  icon: "fa-paper-plane" },
                    opened: { label: `Link opened${opens > 1 ? ` ×${opens}` : ""}`, cls: "bg-sky-100 text-sky-800", icon: "fa-eye" },
                    paid:   { label: "Link paid",   cls: "bg-emerald-100 text-emerald-800", icon: "fa-circle-check" },
                  };
                  const m = map[pls] || { label: pls, cls: "bg-slate-100 text-slate-600", icon: "fa-link" };
                  const opened = a.payment_link_first_opened_at ? new Date(a.payment_link_first_opened_at).toLocaleString() : "";
                  return (
                    <span
                      data-testid={`addon-link-status-${a.id}`}
                      title={opened ? `First opened ${opened}` : "Awaiting customer click"}
                      className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${m.cls}`}
                    >
                      <i className={`fa-solid ${m.icon}`} aria-hidden="true"></i>
                      {m.label}
                    </span>
                  );
                })()}
                {a.payment_status === "paid" && Number(a.amount) > 0 && (
                  <Button data-testid={`addon-refund-${a.id}`} size="sm" variant="outline"
                    className="rounded-full text-xs h-7 border-amber-300 text-amber-800 hover:bg-amber-50"
                    onClick={async () => {
                      const carrierNote = a.supplier_pnr_synced
                        ? "\n\nNOTE: this add-on is already on the airline PNR. You'll need to call the carrier to cancel the SSR after refunding."
                        : "";
                      if (!window.confirm(`Refund $${Number(a.amount).toFixed(2)} to the customer via Stripe?${carrierNote}`)) return;
                      try {
                        const { data } = await api.post(`/admin/addons/${a.id}/refund`);
                        toast.success(`Refund issued · $${Number(data.refund?.amount || 0).toFixed(2)} · ${data.refund?.status || "processing"}`);
                        if (data.manual_carrier_cancel_required) toast.warning("Manual carrier-side SSR cancel still required");
                        load();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
                    }}>
                    <i className="fa-solid fa-rotate-left mr-1" aria-hidden="true"></i> Refund
                  </Button>
                )}
                {a.payment_status !== "paid" && Number(a.amount) > 0 && (
                  <Button data-testid={`addon-send-link-${a.id}`} size="sm" variant="outline"
                    className="rounded-full text-xs h-7 border-teal-300 text-teal-700 hover:bg-teal-50"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/admin/addons/${a.id}/send-payment-link`);
                        toast.success("Payment link emailed to customer");
                        if (data.payment_url) { try { await navigator.clipboard.writeText(data.payment_url); toast.success("Link copied to clipboard"); } catch (_e) { /* clipboard blocked */ } }
                        load();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send link"); }
                    }}>
                    <i className="fa-solid fa-paper-plane mr-1" aria-hidden="true"></i> Send payment link
                  </Button>
                )}
                {a.payment_status !== "paid" && (
                  <Button data-testid={`addon-remove-${a.id}`} size="sm" variant="outline"
                    className="rounded-full text-xs h-7 border-rose-300 text-rose-700 hover:bg-rose-50"
                    onClick={async () => {
                      if (!window.confirm("Remove this add-on?")) return;
                      try { await api.delete(`/admin/addons/${a.id}`); toast.success("Add-on removed"); load(); }
                      catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
                    }}>
                    <i className="fa-solid fa-trash mr-1" aria-hidden="true"></i> Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Refunds */}
      {d.refunds?.length > 0 && (
        <Card className="mt-5 p-5">
          <h2 className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Refunds</h2>
          <ul className="mt-3 space-y-1 text-sm">
            {d.refunds.map(r => <li key={r.id}>${Number(r.amount).toFixed(2)} · {r.note} · {r.processed_by} · {new Date(r.created_at).toLocaleString()}</li>)}
          </ul>
        </Card>
      )}

      {/* Dialogs */}
      <Dialog open={addonOpen} onOpenChange={setAddonOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[var(--brand-accent)]">Service ticket — add seat / bag / meal</DialogTitle>
            <DialogDescription>
              Live add-ons priced directly from {(b.supplier_source || b.supplier || "supplier").toString().toLowerCase()}.
              Selecting a seat / bag / meal is logged against the PNR with the carrier&apos;s live price (no auto-charge — admin chooses whether to invoice the customer).
            </DialogDescription>
          </DialogHeader>
          <AdminAddonPanel
            booking={b}
            onSaved={() => { setAddonOpen(false); load(); }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-xl text-[var(--brand-accent)]">Record refund</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Amount</Label><Input type="number" step="0.01" value={refundForm.amount} onChange={e=>setRefundForm({...refundForm,amount:e.target.value})}/></div>
            <div>
              <Label className="block mb-1.5">Kind</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="bk-rf-kind">
                <button type="button" data-testid="bk-rf-kind-stripe" onClick={() => setRefundForm({ ...refundForm, kind: "stripe" })}
                        className={"border rounded-lg p-2 text-left text-xs " + (refundForm.kind === "stripe" ? "border-indigo-500 bg-indigo-50" : "border-slate-200")}>
                  <div className="font-semibold text-indigo-800">Stripe auto-refund</div>
                  <div className="text-slate-500">Calls Stripe now.</div>
                </button>
                <button type="button" data-testid="bk-rf-kind-manual" onClick={() => setRefundForm({ ...refundForm, kind: "manual" })}
                        className={"border rounded-lg p-2 text-left text-xs " + (refundForm.kind === "manual" ? "border-slate-500 bg-slate-50" : "border-slate-200")}>
                  <div className="font-semibold text-slate-700">Manual ledger</div>
                  <div className="text-slate-500">Bank wire / cash already refunded.</div>
                </button>
              </div>
            </div>
            <div><Label>Note</Label><Textarea rows={2} value={refundForm.note} onChange={e=>setRefundForm({...refundForm,note:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={()=>setRefundOpen(false)}>Cancel</Button><Button data-testid="admin-bk-refund-save" onClick={submitRefund} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
